import dayjs, { Dayjs } from 'dayjs';
import { ScheduleException } from '../../../services/scheduleApi';
import { SegmentedAppointment } from '../segments';
import { TechLane, resolveTechColor } from '../useSchedulingData';
import { jobStatusColor } from '../statusColors';
import { expandExceptionsClient } from '../exceptionSegments';

export interface AgendaViewProps {
  date: Dayjs;
  appts: SegmentedAppointment[];
  techs: TechLane[];
  exceptions: ScheduleException[];
  onOpen: (id: number) => void;
  /** Tap an availability block → edit it. Optional — Scheduling.tsx does not
   *  currently wire block-editing into Agenda (SP-1); tapping a block row is
   *  a no-op until a caller passes this. */
  onEditBlock?: (ex: ScheduleException) => void;
}

interface ApptRow {
  kind: 'appt';
  start: string;
  appt: SegmentedAppointment;
}
interface BlockRow {
  kind: 'block';
  start: string;
  end: string;
  exception: ScheduleException;
}
type Row = ApptRow | BlockRow;

const NO_TECH_COLOR = '#d9d9d9';

function techNameFor(techs: TechLane[], technicianId?: number | null): string {
  if (technicianId == null) return 'Unassigned';
  return techs.find((t) => String(t.id) === String(technicianId))?.name || 'Unassigned';
}

// Mirrors ApptBlock's composeCustomerLabel (company name, else first+last,
// else the appt title) without importing from that module — AgendaView is
// intentionally self-contained so it doesn't create a coupling into the grid
// parts other SP-1 tasks own.
function customerLabel(appt: SegmentedAppointment): string {
  const c: any = appt.customer;
  const composed = c?.company_name || [c?.first_name, c?.last_name].filter(Boolean).join(' ');
  return composed || appt.title || 'Appointment';
}

// Short display label per exception category — mirrors ExceptionBlock's
// categoryMeta (label only; Agenda rows are plain text, no icon).
function categoryLabel(type: ScheduleException['exception_type']): string {
  switch (type) {
    case 'break':
      return 'Break';
    case 'time_off':
      return 'Time off';
    case 'special_hours':
      return 'Hours';
    case 'holiday':
      return 'Holiday';
    default:
      return 'Blocked';
  }
}

interface AgendaApptRowProps {
  appt: SegmentedAppointment;
  /** The visible day's segment start (e.g. `segOnDay.start` from the Row
   *  builder below) — a multi-day continuation day starts at local midnight,
   *  not at appt.start_time (that's the job's overall/first-day start). The
   *  row must display the SAME instant it sorted by, or a Tuesday
   *  continuation of a Mon-2pm job would misleadingly show "2:00 pm". */
  segStart: string;
  techs: TechLane[];
  onOpen: (id: number) => void;
}

function AgendaApptRow({ appt, segStart, techs, onOpen }: AgendaApptRowProps) {
  const { color } = jobStatusColor(appt);
  const techColor = resolveTechColor(techs, appt.technician_id) || NO_TECH_COLOR;
  const timeLabel = dayjs(segStart).format('h:mm a');
  const tech = techNameFor(techs, appt.technician_id);
  const customer = customerLabel(appt);

  const open = () => onOpen(appt.id!);

  return (
    <div
      role="listitem"
      data-testid="agenda-appt-row"
      data-appt-id={appt.id}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') open();
      }}
      tabIndex={0}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '14px 12px',
        minHeight: 56,
        boxSizing: 'border-box',
        borderLeft: `4px solid ${techColor}`,
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
          marginTop: 4,
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{timeLabel}</span>
          <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{tech}</span>
        </div>
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {customer}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'rgba(0,0,0,0.65)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {appt.title}
        </div>
      </div>
    </div>
  );
}

interface AgendaBlockRowProps {
  row: BlockRow;
  onEditBlock?: (ex: ScheduleException) => void;
}

// Diagonal-hatch fill over a faint neutral tint — matches ExceptionBlock's
// "visually unavailable" treatment (never reads as a bookable appointment).
const BLOCK_HATCH =
  'repeating-linear-gradient(45deg, rgba(0,0,0,0.06) 0, rgba(0,0,0,0.06) 6px, transparent 6px, transparent 12px)';

function AgendaBlockRow({ row, onEditBlock }: AgendaBlockRowProps) {
  const label = categoryLabel(row.exception.exception_type);
  const timeLabel = `${dayjs(row.start).format('h:mm a')} – ${dayjs(row.end).format('h:mm a')}`;

  return (
    <div
      role="listitem"
      data-testid="agenda-block-row"
      data-muted="true"
      onClick={() => onEditBlock?.(row.exception)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        minHeight: 44,
        boxSizing: 'border-box',
        borderBottom: '1px solid #f0f0f0',
        background: `${BLOCK_HATCH}, rgba(0,0,0,0.03)`,
        color: 'rgba(0,0,0,0.55)',
        cursor: onEditBlock ? 'pointer' : 'default',
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 600 }}>{timeLabel}</span>
      <span style={{ fontSize: 12 }}>{label}</span>
      {row.exception.reason && <span style={{ fontSize: 12, opacity: 0.8 }}>· {row.exception.reason}</span>}
    </div>
  );
}

// SP-1 Task 8: mobile-default day-to-day agenda list. Consumes the same data
// shape as the grids (SegmentedAppointment[]/TechLane[]/ScheduleException[])
// for a single visible `date` (Scheduling.tsx's `anchor`, stepped a day at a
// time — see ScheduleControls) and renders a flat, chronological list: every
// appt segment landing on that day + every availability-block occurrence
// covering that day, sorted by start time. No multi-day date-group headers
// are needed for the shape Scheduling.tsx actually passes (a single `date`,
// not a range) — if a future caller widens this to a range, group by
// segment/occurrence date before sorting within each group.
export function AgendaView({ date, appts, techs, exceptions, onOpen, onEditBlock }: AgendaViewProps) {
  const dateStr = date.format('YYYY-MM-DD');
  const dateDay = date.startOf('day');

  const apptRows: ApptRow[] = [];
  for (const appt of appts) {
    const segOnDay = appt.segments.find((s) => s.date === dateStr);
    if (!segOnDay) continue;
    apptRows.push({ kind: 'appt', start: segOnDay.start, appt });
  }

  // Expand recurring exceptions for just this day, then keep only occurrences
  // that actually cover it (expandExceptionsClient passes non-recurring rows
  // through unchanged regardless of date, so a day-level cover check is still
  // needed — mirrors bucketExceptionsByDate's per-day membership test).
  const expanded = expandExceptionsClient(exceptions || [], dateStr, dateStr);
  const blockRows: BlockRow[] = [];
  for (const ex of expanded) {
    const s = dayjs(ex.start_datetime);
    const e = dayjs(ex.end_datetime);
    if (!s.isValid() || !e.isValid()) continue;
    const sDay = s.startOf('day');
    const eDay = e.startOf('day');
    if (sDay.isAfter(dateDay, 'day') || eDay.isBefore(dateDay, 'day')) continue;
    const start = sDay.isSame(dateDay, 'day') ? ex.start_datetime : `${dateStr} 00:00:00`;
    const end = eDay.isSame(dateDay, 'day') ? ex.end_datetime : `${dateStr} 23:59:59`;
    blockRows.push({ kind: 'block', start, end, exception: ex });
  }

  const rows: Row[] = [...apptRows, ...blockRows].sort(
    (a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf(),
  );

  return (
    <div data-testid="agenda-view" role="list">
      {rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
          Nothing scheduled
        </div>
      ) : (
        rows.map((row) =>
          row.kind === 'appt' ? (
            <AgendaApptRow
              key={`appt-${row.appt.id}`}
              appt={row.appt}
              segStart={row.start}
              techs={techs}
              onOpen={onOpen}
            />
          ) : (
            <AgendaBlockRow key={`block-${row.exception.id ?? 'x'}-${row.start}`} row={row} onEditBlock={onEditBlock} />
          ),
        )
      )}
    </div>
  );
}

export default AgendaView;
